import {
  VIDEO_EXTENSIONS,
  IMAGE_EXTENSIONS,
  POPULAR_EMAIL_DOMAINS,
} from "./constants";

/**
 * Determine if a URL points to an image, video, or neither
 * @param {string} url - The URL to check
 * @returns {'image' | 'video' | ''} - The type of media
 */
export const detectMediaType = (url) => {
  if (!url) return "";

  const lowerUrl = url.toLowerCase();

  if (IMAGE_EXTENSIONS.some((ext) => lowerUrl.includes(ext))) {
    return "image";
  }

  if (VIDEO_EXTENSIONS.some((ext) => lowerUrl.includes(ext))) {
    return "video";
  }

  return "";
};

/**
 * Validate if email domain is in the popular domains list
 * @param {string} email - The email address to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidEmailFormat = (email) => {
  // Basic format check - must have @ and a dot
  const basicRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!basicRegex.test(email)) return false;

  // Extract domain and check if it's in popular list
  const domain = email.split("@")[1].toLowerCase();
  return POPULAR_EMAIL_DOMAINS.has(domain);
};

/**
 * Replace old CDN URLs with new ones in a value (string or nested structure)
 * @param {any} value - The value to process
 * @param {string} oldUrl - Old URL to replace
 * @param {string} newUrl - New URL to replace with
 * @returns {any} - Processed value with replaced URLs
 */
export const replaceCDNUrl = (value, oldUrl, newUrl) => {
  if (typeof value === "string") {
    return value.replace(oldUrl, newUrl);
  }
  if (Array.isArray(value)) {
    return value.map((item) => replaceCDNUrl(item, oldUrl, newUrl));
  }
  if (typeof value === "object" && value !== null) {
    return Object.entries(value).reduce(
      (acc, [key, val]) => ({
        ...acc,
        [key]: replaceCDNUrl(val, oldUrl, newUrl),
      }),
      {},
    );
  }
  return value;
};

/**
 * Parse boolean string values to actual boolean
 * @param {string} value - The value to parse
 * @returns {string | boolean} - Parsed value
 */
export const parseBooleanString = (value) => {
  if (value === "true") return true;
  if (value === "false") return false;
  return value;
};

/**
 * Build redirect URL with optional protocol
 * @param {string} url - The URL to process
 * @returns {string} - Full URL with protocol
 */
export const buildRedirectUrl = (url) => {
  if (!url) return "";
  if (url.includes("http")) return url;
  return `https://${url}`;
};

/**
 * Get query parameter value from URL
 * @param {string} paramName - The parameter name to get
 * @returns {string | null} - The parameter value or null
 */
export const getQueryParam = (paramName) => {
  const params = new URLSearchParams(window.location.search);
  return params.get(paramName);
};

/**
 * Get all query parameters as an object
 * @returns {object} - Object containing all query parameters
 */
export const getAllQueryParams = () => {
  const params = new URLSearchParams(window.location.search);
  return Object.fromEntries(params.entries());
};
